import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { generateSrcSet, getOptimizedImageUrl, isCloudinaryConfigured } from '@/lib/cloudinary';

interface ResponsiveImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  priority?: boolean;
  cloudinaryId?: string; // Optional Cloudinary public ID
  sizes?: string; // CSS sizes attribute
  widths?: number[]; // Custom width breakpoints
  onLoad?: () => void;
  onError?: () => void;
}

/**
 * Responsive image component with srcset, WebP support, and optional Cloudinary CDN
 * 
 * Usage:
 * 
 * // With local WebP images:
 * <ResponsiveImage src="/image.webp" alt="Description" />
 * 
 * // With Cloudinary CDN (requires VITE_CLOUDINARY_CLOUD_NAME):
 * <ResponsiveImage 
 *   cloudinaryId="folder/image" 
 *   alt="Description"
 *   sizes="(max-width: 768px) 100vw, 50vw"
 * />
 */
export function ResponsiveImage({
  src,
  alt,
  className,
  width,
  height,
  priority = false,
  cloudinaryId,
  sizes = '100vw',
  widths = [320, 640, 768, 1024, 1280, 1920],
  onLoad,
  onError
}: ResponsiveImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const [error, setError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Determine if we should use Cloudinary
  const useCloudinary = cloudinaryId && isCloudinaryConfigured();

  useEffect(() => {
    if (priority) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px'
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    setError(true);
    onError?.();
  };

  if (error) {
    return (
      <div 
        className={cn(
          'flex items-center justify-center bg-muted',
          className
        )}
        style={{ width, height }}
      >
        <span className="text-muted-foreground text-sm">Failed to load</span>
      </div>
    );
  }

  // Generate responsive URLs and srcset
  const imageSrc = useCloudinary 
    ? getOptimizedImageUrl(cloudinaryId, width, height)
    : src;
  
  const imageSrcSet = useCloudinary
    ? generateSrcSet(cloudinaryId, widths)
    : undefined;

  return (
    <div 
      ref={imgRef}
      className={cn('relative overflow-hidden', className)}
      style={{ width, height }}
    >
      {/* Blur placeholder */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}
      
      {/* Actual image */}
      {isInView && (
        <picture>
          {/* WebP source for browsers that support it */}
          {!useCloudinary && src.match(/\.(jpg|jpeg|png)$/i) && (
            <source
              srcSet={src.replace(/\.(jpg|jpeg|png)$/i, '.webp')}
              type="image/webp"
              sizes={sizes}
            />
          )}
          
          {/* Cloudinary responsive images */}
          {useCloudinary && imageSrcSet && (
            <source
              srcSet={imageSrcSet}
              sizes={sizes}
            />
          )}
          
          {/* Fallback image */}
          <img
            src={imageSrc}
            alt={alt}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            fetchPriority={priority ? 'high' : undefined}
            onLoad={handleLoad}
            onError={handleError}
            className={cn(
              'w-full h-full object-cover transition-opacity duration-300',
              isLoaded ? 'opacity-100' : 'opacity-0'
            )}
            width={width}
            height={height}
          />
        </picture>
      )}
    </div>
  );
}
